'use client';

import type { BenchmarkRow, DashboardResponse } from '@ferty9/types';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  buildDashboardView,
  CenterLeaderboardAndJourneyComparison,
  CsatSummaryTables,
  JourneyDetailedMetrics,
  KpiSection,
  NlpAnalysis,
  NpsByServiceCentre,
  NpsDistributionBar,
  NpsHeroBanner,
  OperationalStrategyAdvisor,
  ResponseDistributionCard,
  ServicePerformanceAndJourneyShare,
  StaffLeaderboardCharts,
  TrendMetricCard,
} from '../components/DashboardTab';
import './dashboardPdfReport.css';

interface DashboardPdfPayload {
  dashboard: DashboardResponse;
  benchmark: BenchmarkRow[];
  generatedAt?: string;
}

export function DashboardPdfReport({ token }: { token: string }) {
  const [payload, setPayload] = useState<DashboardPdfPayload | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    document.documentElement.classList.add('dashboard-pdf-document');
    return () => {
      document.documentElement.classList.remove('dashboard-pdf-document');
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadReport() {
      try {
        const response = await fetch(`/api/admin/dashboard/export-data?token=${encodeURIComponent(token)}`, {
          credentials: 'omit',
          cache: 'no-store',
        });
        const data = await response.json() as DashboardPdfPayload & { error?: string };
        if (!response.ok) throw new Error(data.error || `Report request failed with status ${response.status}`);
        if (!cancelled) setPayload(data);
      } catch (loadError) {
        if (!cancelled) setError(loadError instanceof Error ? loadError.message : 'Report data could not be loaded.');
      }
    }
    void loadReport();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const view = useMemo(() => payload ? buildDashboardView(payload.dashboard, payload.benchmark ?? []) : null, [payload]);

  useEffect(() => {
    if (view) {
      const timeout = window.setTimeout(() => {
        document.documentElement.dataset.reportReady = 'true';
      }, 1200);
      return () => window.clearTimeout(timeout);
    }
    return undefined;
  }, [view]);

  if (error) {
    return <div className="pdf-error">Dashboard PDF report could not be generated: {error}</div>;
  }

  if (!view) {
    return <div className="pdf-loading">Preparing dashboard report...</div>;
  }

  const generatedAt = payload?.generatedAt ? new Date(payload.generatedAt) : new Date();

  return (
    <main id="pdf-report-root" className="dashboard-pdf-report" data-report-ready="true">
      <PdfPage generatedAt={generatedAt}>
        <NpsHeroBanner view={view} />
        <OperationalStrategyAdvisor view={view} />
        <KpiSection view={view} />
        <NpsDistributionBar view={view} />
        <SectionTitle title="Trend Analysis & Response Distribution" />
        <div className="charts-grid pdf-chart-grid">
          <TrendMetricCard view={view} metric="nps" />
          <TrendMetricCard view={view} metric="csat" />
        </div>
      </PdfPage>

      <PdfPage generatedAt={generatedAt}>
        <div className="charts-grid pdf-chart-grid pdf-first-grid">
          <TrendMetricCard view={view} metric="volume" />
          <ResponseDistributionCard view={view} />
        </div>
        <ServicePerformanceAndJourneyShare view={view} />
        <CenterLeaderboardAndJourneyComparison view={view} />
      </PdfPage>

      <PdfPage generatedAt={generatedAt}>
        <StaffLeaderboardCharts view={view} />
        <NpsByServiceCentre view={view} />
      </PdfPage>

      <PdfPage generatedAt={generatedAt}>
        <CsatSummaryTables view={view} />
        <JourneyDetailedMetrics view={view} />
        <NlpAnalysis view={view} />
      </PdfPage>
    </main>
  );
}

function PdfPage({ children, generatedAt }: { children: ReactNode; generatedAt: Date }) {
  return (
    <section className="pdf-page">
      <PdfHeader generatedAt={generatedAt} />
      <div className="pdf-page-content">{children}</div>
    </section>
  );
}

function PdfHeader({ generatedAt }: { generatedAt: Date }) {
  return (
    <header className="pdf-report-header">
      <div>
        <h1>Dashboard Overview</h1>
        <p>Ferty9 Fertility Center - Patient Experience NPS Analytics</p>
      </div>
      <div className="pdf-report-meta">
        <span>Dashboard Report</span>
        <p>Report Generated: {generatedAt.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}</p>
      </div>
    </header>
  );
}

function SectionTitle({ title }: { title: string }) {
  return (
    <div className="section-header pdf-section-header">
      <div className="section-title">
        <span className="section-icon">↗</span>
        <h2>{title}</h2>
      </div>
    </div>
  );
}
